import { useCallback } from "react";
import { nakamaService } from "../services/nakama";
import { Inventory, LoginCredentials, RegisterData } from "../types/auth";

export const useAuth = () => {
  const readUserData = useCallback(async (session: any, keyName: string) => {
    try {
      const result = await nakamaService.readStorageObjects(session, {
        object_ids: [
          {
            collection: "userdata",
            key: keyName,
            user_id: session.user_id,
          },
        ],
      });

      if (result.objects && result.objects.length > 0) {
        return result.objects[0].value;
      }
      return null;
    } catch (error) {
      console.error(`Error reading user data for key ${keyName}:`, error);
      throw error;
    }
  }, []);

  // Verificar sesión existente
  const checkExistingSession = useCallback(async () => {
    const token = localStorage.getItem("nakamaToken");
    const refreshToken = localStorage.getItem("nakamaRefreshToken");

    if (!token || !refreshToken) {
      return null;
    }

    try {
      const session = nakamaService.restoreSession(token, refreshToken);
      const account = await nakamaService.getAccount(session);

      // Cargar datos del usuario
      const [profile, vault] = await Promise.all([
        readUserData(session, "profile"),
        readUserData(session, "vault"),
      ]);

      return {
        user: {
          userId: account.user.id,
          username: account.user.username,
          email: account.user.email,
        },
        profile,
        vault: vault || createEmptyVault(),
      };
    } catch (error) {
      console.error("Error restoring session:", error);
      // Limpiar tokens inválidos
      localStorage.removeItem("nakamaToken");
      localStorage.removeItem("nakamaRefreshToken");
      localStorage.removeItem("nakamaUserId");
      return null;
    }
  }, [readUserData]);

  // Login
  const performLogin = useCallback(
    async (credentials: LoginCredentials) => {
      try {
        const session = await nakamaService.authenticateEmail(
          credentials.email,
          credentials.password,
          false,
        );

        // Guardar tokens
        localStorage.setItem("nakamaToken", session.token);
        localStorage.setItem("nakamaRefreshToken", session.refresh_token);
        localStorage.setItem("nakamaUserId", session.user_id);

        // Obtener datos de la cuenta y del storage
        const account = await nakamaService.getAccount(session);
        const [profile, vault] = await Promise.all([
          readUserData(session, "profile"),
          readUserData(session, "vault"),
        ]);

        return {
          user: {
            userId: account.user.id,
            username: account.user.username,
            email: account.user.email,
          },
          profile,
          vault: vault || createEmptyVault(),
        };
      } catch (error: any) {
        let errorMessage = "Credenciales incorrectas o error de conexión";

        if (error.message) {
          try {
            const errorData = JSON.parse(error.message);
            errorMessage = errorData.message || errorMessage;
          } catch {
            errorMessage = error.message;
          }
        }

        throw new Error(errorMessage);
      }
    },
    [readUserData],
  );

  // Registro
  const performRegister = useCallback(async (data: RegisterData) => {
    // Validaciones
    if (data.password.length < 6) {
      throw new Error("La contraseña debe tener al menos 6 caracteres");
    }

    // Validar edad
    const birthDate = new Date(data.birthdate);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    if (age < 13) {
      throw new Error("Debes tener al menos 13 años para registrarte");
    }

    try {
      // Crear cuenta
      const session = await nakamaService.authenticateEmail(
        data.email,
        data.password,
        true,
        data.username,
      );

      // Guardar perfil en storage
      const userProfile = {
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        email: data.email,
        birthdate: data.birthdate,
        country: data.country,
        gender: data.gender,
        age: age,
        registeredAt: new Date().toISOString(),
      };

      await nakamaService.writeStorageObjects(session, [
        {
          collection: "userdata",
          key: "profile",
          value: userProfile,
          permission_read: 1,
          permission_write: 0,
        },
        {
          collection: "userdata",
          key: "vault",
          value: createEmptyVault(),
          permission_read: 1,
          permission_write: 0,
        },
      ]);

      // no voy a guardar los tokens aun para que la pagina no se actualice tan rapido, resulta incomodo
      // localStorage.setItem("nakamaToken", session.token);
      // localStorage.setItem("nakamaRefreshToken", session.refresh_token);
      // localStorage.setItem("nakamaUserId", session.user_id);
    } catch (error: any) {
      let errorMessage = "Error al crear la cuenta";

      if (error.message) {
        try {
          const errorData = JSON.parse(error.message);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = error.message;
        }
      }

      throw new Error(errorMessage);
    }
  }, []);

  // Logout
  const performLogout = useCallback(() => {
    localStorage.removeItem("nakamaToken");
    localStorage.removeItem("nakamaRefreshToken");
    localStorage.removeItem("nakamaUserId");
  }, []);

  return {
    checkExistingSession,
    performLogin,
    performRegister,
    performLogout,
    readUserData,
  };
};

const createEmptyVault = (): Inventory => {
  const vault: Inventory = {};
  for (let row = 1; row <= 3; row++) {
    for (let col = 1; col <= 6; col++) {
      vault[`${col},${row}`] = null;
    }
  }
  return vault;
};
